import { useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import CustomModal from './atoms/CustomModal';
import Button from './atoms/Button';
import Input from './atoms/Input';
import Select from './atoms/Select/Select';
import { useGetAllTicketsQuery, useCreateBatchManualOrdersMutation } from '../store';
import useGetParams from '../hooks/useGetParams';

interface BatchManualOrdersModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormValues {
  ticketId: string;
  name: string;
  amount: number;
}

const BatchManualOrdersModal: React.FC<BatchManualOrdersModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { eventId, businessId } = useGetParams();
  const [selectedTicketId, setSelectedTicketId] = useState<string>('');

  const { data: tickets, isLoading: ticketsLoading } = useGetAllTicketsQuery(
    {
      businessId: businessId || '',
      eventId: eventId || '',
      publicSaleAvailable: false,
    },
    { skip: !businessId || !eventId },
  );

  const [createBatchManualOrders, { isLoading: isCreating }] = useCreateBatchManualOrdersMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<FormValues>({
    defaultValues: {
      ticketId: '',
      name: '',
      amount: 1,
    },
  });

  const handleClose = () => {
    reset();
    setSelectedTicketId('');
    onClose();
  };

  const onSubmit = async (data: FormValues) => {
    try {
      await createBatchManualOrders({
        businessId: businessId || '',
        eventId: eventId || '',
        ticketId: selectedTicketId,
        name: data.name,
        amount: data.amount,
      }).unwrap();

      toast.success(`Se crearon ${data.amount} órdenes manuales exitosamente`);
      handleClose();
    } catch (error) {
      console.error('Error creating batch manual orders:', error);
      toast.error('Error al crear las órdenes manuales');
    }
  };

  const ticketOptions = tickets?.map((ticket) => ({
    value: ticket.id,
    label: ticket.name,
  })) || [];

  return (
    <CustomModal
      isOpen={isOpen}
      onRequestClose={handleClose}
      hideCancelButton
    >
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4 w-[65vw] max-w-[500px]">
        <h1 className="text-text-primary text-xl font-semibold">Crear órdenes manuales en lote</h1>
        <p className="text-text-secondary text-sm">
          Crea múltiples órdenes manuales a la vez seleccionando un tipo de entrada.
        </p>

        <div className="flex flex-col gap-4">
          <Select
            id="ticketId"
            label="Tipo de entrada"
            options={ticketOptions}
            placeholder={ticketsLoading ? 'Cargando...' : 'Seleccionar entrada'}
            value={selectedTicketId}
            onChange={(e) => {
              setSelectedTicketId(e.target.value as string);
              setValue('ticketId', e.target.value as string);
            }}
            errMsg={(!selectedTicketId && errors.ticketId?.message) || undefined}
          />

          <Input
            id="name"
            label="Nombre"
            labelAbove
            {...register('name', { required: 'El nombre es requerido' })}
            errMsg={errors.name?.message}
            placeholder="Nombre del comprador"
          />

          <Input
            id="amount"
            label="Cantidad de órdenes"
            labelAbove
            type="number"
            {...register('amount', {
              required: 'La cantidad es requerida',
              min: { value: 1, message: 'La cantidad mínima es 1' },
              valueAsNumber: true,
            })}
            errMsg={errors.amount?.message}
            placeholder="1"
          />
        </div>

        <div className="flex flex-row gap-2 mt-4">
          <Button variant="secondary" onClick={handleClose} type="button">
            Cancelar
          </Button>
          <Button
            type="submit"
            loading={isCreating}
            disabled={!selectedTicketId || !watch('name') || !watch('amount')}
          >
            Crear órdenes
          </Button>
        </div>
      </form>
    </CustomModal>
  );
};

export default BatchManualOrdersModal;
